/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

/**
 * Returns true if current playhead position is inside and advert break. False otherwise.
 * @param playheadMs the current playhead position in milliseconds
 * @param adBreak the advert break to check whether the playhead position is within the
 * advert break start and advert break end time or not
 */
export function isInAdvertBreak(playheadMs: number, adBreak: any): boolean {
  return adBreak.adStartTimeOffsetMs <= playheadMs && playheadMs < adBreak.adStartTimeOffsetMs + adBreak.durationMs;
}

/**
 * Returns the first advert break whose start position is 0L.
 * @param timeline an array of advert breaks
 */
export function findPrerollAdvertBreakByTimeline(timeline: any[]): any {
  return timeline.find((advertBreak: any) => advertBreak.adStartTimeOffsetMs === 0);
}

/**
 * Returns a preroll advert break if provided playhead position is inside an active
 * advert break.
 * @param playheadMs the current playhead position in milliseconds
 * @param timeline an array of advert breaks
 */
export function findPrerollAdvertBreakByPlayheadMsAndTimeline(playheadMs: number, timeline: any[]): any {
  const prerollAdvertBreak = findPrerollAdvertBreakByTimeline(timeline);

  if (prerollAdvertBreak && isInAdvertBreak(playheadMs, prerollAdvertBreak) && prerollAdvertBreak.isActive) {
    return prerollAdvertBreak;
  }

  return null;
}

/**
 * Returns an array of advert breaks whose playback time is prior to the provided playhead
 * position.
 * @param positionMs the current playhead position in milliseconds
 * @param timeline an array of advert breaks
 */
export function advertBreakDurationsPriorTo(positionMs: number, timeline: any = []): number {
  let offset = timeline
    .filter((advertBreak: any) => advertBreak.adStartTimeOffsetMs + advertBreak.durationMs < positionMs)
    .map((advertBreak: any) => advertBreak.durationMs)
    .reduce((a: any, b: any) => a + b, 0);

  offset += timeline
    .filter((advertBreak: any) => isInAdvertBreak(positionMs, advertBreak))
    .map((advertBreak: any) => positionMs - advertBreak.adStartTimeOffsetMs)
    .reduce((a: any, b: any) => a + b, 0);

  return offset;
}

/**
 * Search for the first postroll advert break. Once the advert break is found, this function
 * returns the advert break if the current playhead position is within the advert break's
 * time range.
 * @param playheadMs the current playhead position in milliseconds
 * @param timeline an array of advert breaks
 * @param sessionDurationMs the session duration in milliseconds
 */
export function findPostrollAdvertBreak(playheadMs: number, timeline: any[], sessionDurationMs: number): any {
  const postRollAdvertBreak = timeline.find(
    (advertBreak: any) => advertBreak.adStartTimeOffsetMs + advertBreak.durationMs === sessionDurationMs,
  );

  if (postRollAdvertBreak && isInAdvertBreak(playheadMs, postRollAdvertBreak)) {
    return postRollAdvertBreak;
  }
  return null;
}

/**
 * Returns the active advert break in which the provided playhead position is within
 * the advert break's time range.
 * @param playheadMs the current playhead position in milliseconds
 * @param timeline an array of advert breaks
 */
export function findActiveAdvertBreak(playheadMs: number, timeline: any[]): any {
  return timeline.find((advertBreak: any) => isInAdvertBreak(playheadMs, advertBreak) && advertBreak.isActive);
}

/**
 * Returns the closest active advert break to the provided playhead position.
 * @param positionMs the current playhead position in milliseconds
 * @param timeline an array of advert breaks
 */
export function findClosestActiveAdvertBreakPriorTo(positionMs: number, timeline: any[]): any {
  let closest = null;

  timeline.forEach((advertBreak: any) => {
    if (advertBreak.adStartTimeOffsetMs + advertBreak.durationMs < positionMs && advertBreak.isActive) {
      closest = advertBreak;
    }
  });

  return closest;
}

/**
 * Returns the first midroll advert break within the provided timeline.
 * @param timeline an array of advert breaks
 */
export function findFirstMidrollAdvertBreak(timeline: any[]): any {
  return timeline.find((advertBreak: any) => advertBreak.adStartTimeOffsetMs > 0);
}
